import { Box, Icon, Skeleton, Stack, Typography } from '@mui/material';
import { AdmissionAggregateTotal, AdmissionMetric, AdmissionMetricEnum } from '@schooly/api';
import { ArrowRightIcon, InformationBlock } from '@schooly/style';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { TrendComponent } from './DashboardStatistics';
import { calculateConversionRate, getMetricData } from './utils';

const CONVERSION_RATE_CONFIGS: Array<{
  metric: AdmissionMetric;
  comparedMetric: AdmissionMetric;
}> = [
  {
    metric: AdmissionMetricEnum.Applied,
    comparedMetric: AdmissionMetricEnum.Offered,
  },
  {
    metric: AdmissionMetricEnum.Offered,
    comparedMetric: AdmissionMetricEnum.Accepted,
  },
  {
    metric: AdmissionMetricEnum.Applied,
    comparedMetric: AdmissionMetricEnum.Accepted,
  },
];

type DashboardConversionRatesProps = {
  isLoading: boolean;
  total: AdmissionAggregateTotal[];
  compareLabel: string;
};

export const DashboardConversionRates: FC<DashboardConversionRatesProps> = ({
  isLoading,
  total,
  compareLabel,
}) => {
  const { formatMessage } = useIntl();
  return (
    <Stack
      flex={1}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.common.light2}`,
        borderRadius: '8px',
        padding: theme.spacing(2),
      })}
    >
      <Typography variant="h3">
        {formatMessage({ id: 'dashboard-conversion-rates-title' })}
      </Typography>

      {!isLoading ? (
        <>
          <Stack gap={3} pt={3}>
            {CONVERSION_RATE_CONFIGS.map((config) => (
              <ConversionRate
                key={config.metric}
                config={config}
                total={total}
                compareLabel={compareLabel}
              />
            ))}
          </Stack>
          <InformationBlock
            sx={{
              mt: 3,
              p: 1.5,
              border: (theme) => `1px solid ${theme.palette.common.light2}`,
              justifyContent: 'flex-start',
            }}
          >
            <Typography color="primary.main">
              {formatMessage({ id: 'dashboard-conversion-rates-info' })}
            </Typography>
          </InformationBlock>
        </>
      ) : (
        <Stack gap={3} pt={3}>
          {Array.from({ length: CONVERSION_RATE_CONFIGS.length }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={112}
              sx={{ width: '100%' }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

type ConversionRateProps = {
  config: {
    metric: AdmissionMetric;
    comparedMetric: AdmissionMetric;
  };
  total: AdmissionAggregateTotal[];
  compareLabel: string;
};

const ConversionRate: FC<ConversionRateProps> = ({ config, total, compareLabel }) => {
  const { formatMessage } = useIntl();

  const targetMetric = getMetricData(total, config.comparedMetric);
  const sourceMetric = getMetricData(total, config.metric);

  const conversionData = calculateConversionRate(
    targetMetric?.value ?? null,
    sourceMetric?.value ?? null,
    targetMetric?.reference_value ?? null,
    sourceMetric?.reference_value ?? null,
  );

  const { displayPercentage, displayText, tooltipKey, backgroundColor, icon, color } =
    conversionData;

  const tooltipTitle = tooltipKey ? formatMessage({ id: tooltipKey }) : '';

  return (
    <Stack width="100%" gap={0.5}>
      <Stack
        direction="row"
        gap={1}
        sx={{
          '& .MuiTypography-root': {
            color: 'common.grey2',
          },
        }}
      >
        <Typography variant="h3">
          {formatMessage({ id: `dashboard-metric-${config.metric}` })}
        </Typography>
        <Icon
          sx={{
            color: 'common.grey',
          }}
        >
          <ArrowRightIcon />
        </Icon>
        <Typography variant="h3">
          {formatMessage({ id: `dashboard-metric-${config.comparedMetric}` })}
        </Typography>
      </Stack>
      <Stack direction="row" gap={1} pt={0.5} alignItems="center">
        <Typography variant="h2">{displayPercentage}</Typography>
        <TrendComponent
          label={displayText}
          tooltipTitle={tooltipTitle}
          backgroundColor={backgroundColor}
          color={color}
          icon={icon}
        />
      </Stack>

      <Typography color="common.grey2">
        {formatMessage({ id: 'dashboard-compare-vs' })}{' '}
        {conversionData.oldPercentage !== null
          ? `${conversionData.oldPercentage.toFixed(1).replace('.', ',')}%`
          : '-'}{' '}
        {formatMessage({ id: 'dashboard-compared-with' }).toLowerCase()} {compareLabel}
      </Typography>

      <Box
        position="relative"
        sx={{ width: '100%', height: '8px', backgroundColor: 'common.light2', borderRadius: '8px' }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width:
              conversionData.currentPercentage !== null
                ? `${Math.min(conversionData.currentPercentage, 100)}%`
                : '0%',
            height: '100%',
            borderRadius: 'inherit',
            backgroundColor: 'primary.main',
            transition: 'width 0.3s ease-in-out',
          }}
        />
      </Box>
    </Stack>
  );
};
